var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { loadStripeOnramp } from '@stripe/crypto';
import { Network, OnRampAsset, OnRampProvider, OnRampPurchaseStatus } from '@usecapsule/web-sdk';
import { CpslSpinner } from '@usecapsule/react-components';
import { SpinnerContainer } from './common.js';
import styled from 'styled-components';
export const STRIPE_PUBLISHABLE_KEY = 'pk_live_51MvquNGrzDeP5yP9EgVSMBPQbrbg0oHDjPIIXypePd0jzOFjbadyfO7wBKLHhUtbKIUiEUVC3YYcTJyAmJ8xA7JE00T2UDfYKz';
export const STRIPE_PUBLISHABLE_KEY_TEST = 'pk_test_51Q2z5k2epVn9Kq0ZSbUzuetKb4azH6E6UPcvWmjRxvHmxLAJbIK0pwDbPQo63W9OyDng337ntNu3ZXx3AJ7MhFmR00XgfRmPgP';
const AssetCodes = {
    eth: OnRampAsset.ETHEREUM,
    matic: OnRampAsset.POLYGON,
    sol: OnRampAsset.SOLANA,
    usdc: OnRampAsset.USDC,
};
const NetworkCodes = {
    base: Network.BASE,
    ethereum: Network.ETHEREUM,
    polygon: Network.POLYGON,
    solana: Network.SOLANA,
};
const CryptoElementsContext = React.createContext(null);
CryptoElementsContext.displayName = 'CryptoElementsContext';
const useOnrampSessionListener = (type, session, callback) => {
    React.useEffect(() => {
        if (session && callback) {
            const listener = e => callback(e.payload);
            session.addEventListener(type, listener);
            return () => {
                session.removeEventListener(type, listener);
            };
        }
        return () => { };
    }, [session, callback, type]);
};
export const StripeEmbed = ({ capsule, isDark, isEmbedded, onRampPurchase, setOnRampPurchase }) => {
    const [isReady, setIsReady] = useState(false);
    const isStripeEmbed = useMemo(() => onRampPurchase.provider === OnRampProvider.STRIPE, [onRampPurchase]);
    const clientSecret = useMemo(() => ((onRampPurchase === null || onRampPurchase === void 0 ? void 0 : onRampPurchase.provider) === OnRampProvider.STRIPE ? onRampPurchase === null || onRampPurchase === void 0 ? void 0 : onRampPurchase.providerKey : undefined), [onRampPurchase === null || onRampPurchase === void 0 ? void 0 : onRampPurchase.provider, onRampPurchase === null || onRampPurchase === void 0 ? void 0 : onRampPurchase.providerKey]);
    const [stripeOnRamp, setStripeOnRamp] = useState();
    const onrampElementRef = React.useRef(null);
    const [session, setSession] = React.useState();
    useEffect(() => {
        const containerRef = onrampElementRef.current;
        if (containerRef) {
            // NB: ideally we want to be able to hot swap/update onramp iframe
            // This currently results a flash if one needs to mint a new session when they need to udpate fixed transaction details
            containerRef.innerHTML = '';
            if (clientSecret && stripeOnRamp) {
                setSession(stripeOnRamp
                    .createSession({
                    clientSecret,
                    appearance: { theme: isDark ? 'dark' : 'light' },
                })
                    .mount(containerRef));
            }
        }
    }, [clientSecret, stripeOnRamp]);
    useEffect(() => {
        loadStripeOnramp((onRampPurchase === null || onRampPurchase === void 0 ? void 0 : onRampPurchase.testMode) ? STRIPE_PUBLISHABLE_KEY_TEST : STRIPE_PUBLISHABLE_KEY).then(setStripeOnRamp);
    }, []);
    const onReady = useCallback(() => {
        setIsReady(true);
    }, []);
    const onSessionChange = useCallback((_a) => __awaiter(void 0, [_a], void 0, function* ({ session }) {
        if (!isStripeEmbed) {
            return;
        }
        switch (session.status) {
            case 'fulfillment_processing':
            case 'fulfillment_complete':
                const updatedPurchase = yield capsule.ctx.capsuleClient.updateOnRampPurchase({
                    userId: capsule.getUserId(),
                    walletId: onRampPurchase.walletId,
                    externalWalletAddress: onRampPurchase.externalWalletAddress,
                    purchaseId: onRampPurchase.id,
                    updates: {
                        status: OnRampPurchaseStatus.FINISHED,
                        fiatQuantity: session.quote.source_amount,
                        fiat: session.quote.source_currency.asset_code,
                        network: NetworkCodes[session.quote.destination_currency.currency_network],
                        asset: AssetCodes[session.quote.destination_currency.asset_code],
                        assetQuantity: session.quote.destination_amount,
                        providerKey: null,
                    },
                });
                setOnRampPurchase(updatedPurchase);
                if (!isEmbedded) {
                    setTimeout(() => {
                        window.close();
                    }, 5000);
                }
                break;
            default:
                break;
        }
    }), [isStripeEmbed]);
    useOnrampSessionListener('onramp_ui_loaded', session, onReady);
    useOnrampSessionListener('onramp_session_updated', session, onSessionChange);
    return (_jsxs(OuterContainer, { children: [_jsx(Container, { isReady: isReady, ref: onrampElementRef }), !isReady && (_jsx(SpinnerContainer, { style: { width: '100%', height: '100%', flex: 1 }, children: _jsx(CpslSpinner, { size: 100 }) }))] }));
};
const OuterContainer = styled.div `
  height: 100%;
  width: 100%;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;
const Container = styled.div `
  height: 100%;
  width: 100%;
  display: ${({ isReady }) => (isReady ? 'block' : 'none')};

  & > iframe {
    height: 100%;
  }
`;
